/**
 * The status bar (Requirement 1.10).
 *
 * Shows the five numbers Requirement 1.10 names for the current selection — row
 * count, column count, sum, average, and non-empty count — plus a message slot the
 * shell uses for notices such as the 10-sheet rejection (Requirement 1.11) and
 * long-operation progress (Requirement 14.7).
 *
 * The message slot is an `aria-live` region, so a notice reaches a screen-reader
 * user without stealing focus. The aggregate itself is *not* live: it changes on
 * every arrow key, and announcing it each time would drown out everything else.
 */

import { formatAggregate, type AggResult } from '../grid/aggregate.js';
import { el } from './dom.js';

export interface StatusBar {
  element: HTMLElement;
  /** Renders a selection aggregate, or clears it. */
  setAggregate(agg: AggResult | null): void;
  /** Shows a transient notice. Empty string clears it. */
  setMessage(text: string): void;
  /**
   * Shows progress for a long operation, or clears it with `null`
   * (Requirement 14.7).
   *
   * The indicator is a separate slot from the notice so a sort's progress cannot erase
   * the message a user is reading, and it is *not* an `aria-live` region: an operation
   * reporting every 100 ms would otherwise talk over everything else. The completion is
   * announced through the notice instead.
   */
  setProgress(progress: ProgressState | null): void;
}

/** What the progress slot shows. `total` is `null` when the work is uncountable. */
export interface ProgressState {
  /** Operation label, e.g. `sort` or `command:setRegion`. */
  op: string;
  done: number;
  total: number | null;
}

export function createStatusBar(): StatusBar {
  const aggregate = el('span', {
    className: 'status-aggregate',
    testid: 'status-aggregate',
  });
  const message = el('span', {
    className: 'status-message',
    testid: 'status-message',
    role: 'status',
    attrs: { 'aria-live': 'polite' },
  });
  const progress = el('span', {
    className: 'status-progress',
    testid: 'status-progress',
    attrs: { hidden: 'hidden' },
  });
  const element = el('div', { className: 'status-bar', testid: 'status-bar' }, [
    message,
    progress,
    aggregate,
  ]);

  return {
    element,

    setAggregate(agg: AggResult | null): void {
      aggregate.textContent = agg === null ? '' : formatAggregate(agg);
    },

    setMessage(text: string): void {
      message.textContent = text;
    },

    setProgress(state: ProgressState | null): void {
      if (state === null) {
        progress.textContent = '';
        progress.setAttribute('hidden', 'hidden');
        return;
      }
      const pct =
        state.total === null || state.total <= 0
          ? null
          : Math.min(100, Math.round((state.done / state.total) * 100));
      progress.textContent =
        pct === null
          ? `${labelOf(state.op)}… ${state.done.toLocaleString()}`
          : `${labelOf(state.op)}… ${pct}%`;
      progress.removeAttribute('hidden');
    },
  };
}

/** A readable name for an operation id such as `command:setRegion`. */
function labelOf(op: string): string {
  const [head, tail] = op.split(':');
  const name = tail ?? head ?? op;
  return name.charAt(0).toUpperCase() + name.slice(1);
}
